import * as React from 'react';
import { Vector2, Vector3, Color, Quaternion } from '../utils/transform';
import { MarkSceneGraphDirty, RetireSgid, BuildSgNode, CreateSgNode } from './sgproto';

declare global
{
	namespace JSX
	{
		interface IntrinsicElements
		{
			"vsg-node": any;
		}
	}
}

export interface OptionalVector2
{
	x?: number;
	y?: number;
}

export interface OptionalVector3
{
	x?: number;
	y?: number;
	z?: number;
}

export interface OptionalColor
{
	r?: number;
	g?: number;
	b?: number;
}

export function FillOptionalVector2( v: OptionalVector2, def: Vector2 ): Vector2
{
	if ( !v )
	{
		return def;
	}

	return {
		x: v.x ? v.x : def.x,
		y: v.y ? v.y : def.y,
	};
}

export function FillOptionalVector3( v: OptionalVector3, def: Vector3 ): Vector3
{
	if ( !v )
	{
		return def;
	}

	return {
		x: v.x ? v.x : def.x,
		y: v.y ? v.y : def.y,
		z: v.z ? v.z : def.z,
	};
}

export function FillOptionalColor( c: OptionalColor, def: Color ): Color
{
	if ( !c )
	{
		return def;
	}

	return {
		r: c.r ? c.r : def.r,
		g: c.g ? c.g : def.g,
		b: c.b ? c.b : def.b,
	};
}

export function Vector2ToArray( v: Vector2 ): [number, number]
{
	if ( !v )
	{
		return undefined;
	}

	return [v.x, v.y];
}

export function Vector3ToArray( v: Vector3 ): [number, number, number]
{
	if ( !v )
	{
		return undefined;
	}

	return [v.x, v.y, v.z];
}

export function Vector2ToString( v: Vector2 ): string
{
	return Vector2ToArray( v )?.join( " " );
}

export function Vector3ToString( v: Vector3 ): string
{
	return Vector3ToArray( v )?.join( " " );
}

export function QuaternionToString( q: Quaternion ): string
{
	if ( !q )
	{
		return undefined;
	}

	return q.w + " " + q.x + " " + q.y + " " + q.z;
}


export interface SGBaseProps
{
	id?: string;
}

export class SGBase<TProps extends SGBaseProps, TState> extends React.Component<TProps, TState>
{
	private static s_NextSGID: number = 1;
	private m_domRef = React.createRef<HTMLElement>();
	private m_buildNodeOverride: BuildSgNode = null;
	protected m_SGID: number;

	constructor( props: any )
	{
		super( props );
		this.m_SGID = SGBase.s_NextSGID;
		SGBase.s_NextSGID = ( SGBase.s_NextSGID + 1 ) % 4294967296;
	}

	public setBuildNodeOverride( buildNodeOverride: BuildSgNode )
	{
		this.m_buildNodeOverride = buildNodeOverride;
	}

	public getSGID(): number
	{
		return this.m_SGID;
	}

	public getNodeType(): string
	{
		return "base";
	}

	protected createSgNode( domNode: HTMLElement )
	{
		return CreateSgNode( this.getNodeType(), domNode );
	}

	protected getCurrentRootElement(): HTMLElement
	{
		return this.m_domRef.current;
	}

	public componentDidMount()
	{
		if ( this.m_buildNodeOverride )
		{
			( this.m_domRef.current as any ).buildNode = this.m_buildNodeOverride;
		}
		MarkSceneGraphDirty();
	}

	public componentDidUpdate()
	{
		MarkSceneGraphDirty();
	}

	public componentWillUnmount()
	{
		RetireSgid( this.m_SGID );
		if ( this.m_domRef.current && this.m_buildNodeOverride )
		{
			delete ( this.m_domRef.current as any ).buildNode;
		}
	}

	public render()
	{
		return React.cloneElement(
			this.internalRender(),
			{
				id: this.props.id,
				"vsg-type": this.getNodeType(),
				sgid: this.m_SGID,
				ref: this.m_domRef,
			} );
	}

	public internalRender(): JSX.Element
	{
		if ( !this.m_buildNodeOverride )
		{
			return null;
		}
		else
		{
			return ( <vsg-node>
				{ this.props.children }
			</vsg-node> );
		}
	}
}

